import { getFirstReferralCode } from "@/cart/referralCode";
import { syncReferralRewardWithRollback } from "@/cart/maticReferralReward";
import type { CartState } from "@/cart/types";
import type { DisplayCartItem } from "@/cart/displayCart";
import { getLineEffectiveUnitAmount } from "@/cart/displayCart";
import { getCartBundle } from "@/cart/cartBundle";
import GRAPHQL_CLIENT from "@/graphql/client";
import type { Cart } from "@/graphql/storefront/generated";
import {
  isLegacyReferralReward,
  isMatic,
  isReferralRewardBagPass,
} from "@/utils/ShopifyHelpers";
import { syncPerMaticAddOnsToMatics } from "@/cart/syncPerMaticAddOnsToMatics";
import { raiseAddOnsWithMatics } from "@/cart/raiseAddOnsWithMatics";

type GroupedCartMutationHelpersArgs = {
  getCartState: () => CartState;
  setCartState: (state: Partial<CartState>) => void;
  removeItem: (lineId: string) => Promise<void>;
};

type UpdateGroupedCartItemArgs = GroupedCartMutationHelpersArgs & {
  item: DisplayCartItem;
  newQuantity: number;
  updateCartItem: (itemId: string, newQuantity: number) => Promise<void>;
};

type RemoveGroupedCartItemArgs = GroupedCartMutationHelpersArgs & {
  item: DisplayCartItem;
};

type UpdateGroupedCartItemVariantArgs = Pick<
  GroupedCartMutationHelpersArgs,
  "getCartState" | "setCartState"
> & {
  item: DisplayCartItem;
  variantId: string;
};

export const updateGroupedCartItem = async ({
  item,
  newQuantity,
  getCartState,
  setCartState,
  updateCartItem,
}: UpdateGroupedCartItemArgs) => {
  if (item.lines.length === 1) {
    await updateCartItem(item.item.id, newQuantity);
    return;
  }

  const { cartId, cart: currentCart, referralCode } = getCartState();

  if (!cartId || !currentCart) {
    return;
  }

  const syncReward = syncReferralRewardWithRollback({
    get: getCartState,
    set: setCartState,
  });
  const sortedLines = [...item.lines].sort((a, b) => {
    const unitPriceDiff =
      getLineEffectiveUnitAmount(a) - getLineEffectiveUnitAmount(b);

    if (unitPriceDiff !== 0) {
      return unitPriceDiff;
    }

    return a.id.localeCompare(b.id);
  });
  const cartBeforeUpdate = currentCart;
  let remainingQuantity = newQuantity;
  const lineUpdates: Array<{ id: string; quantity: number }> = [];
  const lineIdsToRemove: string[] = [];

  if (getCartBundle(item.item) !== null) {
    // Every line of a bundle holds the bundle's count.
    for (const line of item.lines) {
      if (newQuantity === 0) {
        lineIdsToRemove.push(line.id);
      } else if (line.quantity !== newQuantity) {
        lineUpdates.push({ id: line.id, quantity: newQuantity });
      }
    }
  } else {
    sortedLines.forEach((line, index) => {
      const currentQuantity = line.quantity || 0;
      const nextQuantity =
        index === sortedLines.length - 1
          ? remainingQuantity
          : Math.min(currentQuantity, remainingQuantity);

      remainingQuantity -= nextQuantity;

      if (nextQuantity === 0) {
        lineIdsToRemove.push(line.id);
      } else if (nextQuantity !== currentQuantity) {
        lineUpdates.push({ id: line.id, quantity: nextQuantity });
      }
    });
  }

  let nextCart = currentCart;

  if (lineUpdates.length > 0) {
    const { cartLinesUpdate } = await GRAPHQL_CLIENT.UpdateCartLine({
      cartId,
      lines: lineUpdates,
    });

    const updatedCart = cartLinesUpdate?.cart as Cart | undefined;

    if (!updatedCart) {
      throw new Error("updateGroupedCartItem: updating grouped lines failed");
    }

    nextCart = updatedCart;
  }

  if (lineIdsToRemove.length > 0) {
    const { cartLinesRemove } = await GRAPHQL_CLIENT.RemoveFromCart({
      cartId: nextCart.id,
      lineIds: lineIdsToRemove,
    });

    const updatedCart = cartLinesRemove?.cart as Cart | undefined;

    if (!updatedCart) {
      throw new Error("updateGroupedCartItem: removing grouped lines failed");
    }

    nextCart = updatedCart;
  }

  if (isMatic(item.item)) {
    const previousQuantity = item.lines.reduce(
      (total, line) => total + (line.quantity || 0),
      0,
    );
    const addedMatics = newQuantity - previousQuantity;

    if (addedMatics > 0) {
      try {
        nextCart = await raiseAddOnsWithMatics(nextCart, addedMatics);
      } catch (error) {
        console.error("Failed to raise add-ons after Matic increment:", error);
      }
    }

    try {
      nextCart = await syncPerMaticAddOnsToMatics(nextCart);
    } catch (error) {
      console.error("Failed to sync add-ons after Matic update:", error);
    }

    await syncReward(cartBeforeUpdate, nextCart);
    return;
  }

  setCartState({
    cartId: nextCart.id,
    cart: nextCart,
    referralCode: getFirstReferralCode(nextCart) || referralCode,
  });
};

export const removeGroupedCartItem = async ({
  item,
  getCartState,
  setCartState,
  removeItem,
}: RemoveGroupedCartItemArgs) => {
  if (item.lines.length === 1) {
    await removeItem(item.item.id);
    return;
  }

  const { cartId, cart: currentCart, referralCode } = getCartState();

  if (!cartId || !currentCart) {
    return;
  }

  const lineIdsToRemove = new Set(item.lines.map((line) => line.id));

  if (isMatic(item.item)) {
    currentCart.lines.edges.forEach(({ node }) => {
      if (isLegacyReferralReward(node) || isReferralRewardBagPass(node)) {
        lineIdsToRemove.add(node.id);
      }
    });
  }

  const { cartLinesRemove } = await GRAPHQL_CLIENT.RemoveFromCart({
    cartId,
    lineIds: Array.from(lineIdsToRemove),
  });
  let nextCart = cartLinesRemove?.cart as Cart | undefined;

  if (!nextCart) {
    throw new Error("removeGroupedCartItem: removing grouped lines failed");
  }

  if (isMatic(item.item)) {
    try {
      nextCart = await syncPerMaticAddOnsToMatics(nextCart);
    } catch (error) {
      console.error("Failed to sync add-ons after Matic removal:", error);
    }
  }

  setCartState({
    cartId: nextCart.id,
    cart: nextCart,
    referralCode: getFirstReferralCode(nextCart) || referralCode,
  });
};

export const updateGroupedCartItemVariant = async ({
  item,
  variantId,
  getCartState,
  setCartState,
}: UpdateGroupedCartItemVariantArgs) => {
  const { cartId, referralCode } = getCartState();

  if (!cartId) {
    return;
  }

  const { cartLinesUpdate } = await GRAPHQL_CLIENT.UpdateCartLine({
    cartId,
    lines: item.lines.map((line) => ({
      id: line.id,
      merchandiseId: variantId,
      quantity: line.quantity,
    })),
  });
  const nextCart = cartLinesUpdate?.cart as Cart | undefined;

  if (!nextCart) {
    throw new Error(
      "updateGroupedCartItemVariant: updating grouped line variants failed",
    );
  }

  setCartState({
    cartId: nextCart.id,
    cart: nextCart,
    referralCode: getFirstReferralCode(nextCart) || referralCode,
  });
};
